import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Clipboard } from 'lucide-react';
import { MainBodyData } from '../data/types';
import InvestigationQuestionsModal from './InvestigationQuestionsModal';

interface MainBodyProps {
  data: MainBodyData;
  investigationQuestions?: any;
}

const MainBody: React.FC<MainBodyProps> = ({ data, investigationQuestions }) => {
  const { number, title, icon: Icon, sections } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  return (
    <>
      <div className="min-h-screen bg-white dark:bg-slate-900 p-8">
        <div className="max-w-5xl">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white font-bold text-xl">
              {number}
            </div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100">{title}</h1>
          </div>
          
          <div className="h-1 w-full bg-gradient-to-r from-orange-500 to-amber-600 mb-12" />
          
          <div className="grid grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Overview</h2>
                <p className="text-gray-700 dark:text-slate-300 leading-relaxed">{sections.overview}</p>
              </div>
              
              {sections.note && (
                <div className="border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-500/10 p-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-2">Important Note</h3>
                      <p className="text-gray-700 dark:text-slate-300">{sections.note}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Investigation Steps</h2>
                <div className="space-y-4">
                  {sections.steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 pt-2">
                        <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-1">{step.title}</h3>
                        <p className="text-gray-600 dark:text-slate-400">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-3 mb-4">
                  <Icon className="w-6 h-6 text-orange-500" />
                  <h3 className="font-bold text-slate-900 dark:text-slate-100">Platform Coverage</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {sections.platforms.map((platform, i) => (
                    <div key={i} className="bg-white dark:bg-slate-800 rounded p-3 text-center font-medium text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700">
                      {platform}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-slate-900 dark:bg-slate-950 rounded-lg p-6 text-white">
                <h3 className="font-bold mb-4 text-orange-400 uppercase text-sm tracking-wider">Key Data Points</h3>
                <div className="space-y-3">
                  {sections.dataPoints.map((point, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {investigationQuestions && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  <Clipboard className="w-5 h-5" />
                  Investigation Checklist
                </button>
              )}
              
              <div className="bg-gray-100 dark:bg-slate-800 rounded-lg overflow-hidden border border-gray-300 dark:border-slate-700 h-64 flex items-center justify-center text-gray-400 dark:text-slate-500">
                <Icon className="w-20 h-20" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {investigationQuestions && (
        <InvestigationQuestionsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={title}
          questions={investigationQuestions}
        />
      )}
    </>
  );
};

export default MainBody;